import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  modifyCloudIdentityGroupRoles,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import {
  CloudIdentityGroupRolesUpdateModel,
  DirectoryGroup,
} from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { cloudidentity_v1 } from '@googleapis/cloudidentity';

import Alert from '@mui/material/Alert';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface ChangeGroupCIMemberRoleDialogProps {
  group: DirectoryGroup;
  open: boolean;
  handleClose: () => void;
  selectedRows: string[]; // member emails
  groupEmail: string;
  memberships: cloudidentity_v1.Schema$Membership[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
  customerId: string;
}

export default function ChangeGroupCIMemberRoleDialog({
  open,
  handleClose,
  setSelectedRows,
  selectedRows,
  groupEmail,
  memberships,
  changeLogRoles,
  customerId,
}: ChangeGroupCIMemberRoleDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [selectedRole, setSelectedRole] = useState<
    'MEMBER' | 'OWNER' | 'MANAGER'
  >('MEMBER');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const selectedMembers = memberships.filter((member) =>
    selectedRows.includes(member.name ?? '')
  );

  // The domain-wide "all current and future users" membership doesn't
  // reliably resolve a preferredMemberKey from the Cloud Identity API -
  // treat a missing id the same as an id that correctly resolved to
  // customerId, rather than only matching on the resolved case.
  const isDomainWideMember = (member: cloudidentity_v1.Schema$Membership) =>
    !member.preferredMemberKey?.id ||
    member.preferredMemberKey.id === customerId;

  // groups and member with customerid/All current and future users within domain cannot be set to any role other than MEMBER
  const noGroupMembers = selectedMembers.filter(
    (member) => member.type !== 'GROUP' && !isDomainWideMember(member)
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const request: CloudIdentityGroupRolesUpdateModel[] = noGroupMembers.map(
        (member) => {
          const nameParts = member.name!.split('/');
          const groupId = nameParts[1];
          const membershipId = nameParts[3];
          const oldRoles =
            member.roles
              ?.map((role) => role.name!)
              .filter((name): name is string => !!name) || [];
          const hasMemberExpiry =
            member.roles?.some(
              (role) => role.name === 'MEMBER' && role.expiryDetail != null
            ) ?? false;

          return {
            groupId: groupId,
            membershipId: membershipId,
            memberEmail: member.preferredMemberKey?.id ?? '',
            newRole: selectedRole,
            oldRoles: oldRoles,
            changeLogMessage: changeLogMessage,
            hasMemberExpiry,
          };
        }
      );

      return modifyCloudIdentityGroupRoles(request, groupEmail);
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: noGroupMembers.length > 1 ? 'info' : 'success',
        message:
          noGroupMembers.length > 1
            ? 'The task to update the group members roles has begun.'
            : 'Group members role(s) updated successfully!',
      });

      if (noGroupMembers.length <= 1) {
        queryClient.setQueryData<
          cloudidentity_v1.Schema$Membership[] | undefined
        >([UserKeys.GroupMembersCloudIdentity, groupEmail], (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const newMemberships = oldData.map((membership) => {
            if (
              selectedRows.includes(membership.name ?? '') &&
              membership.type !== 'GROUP' &&
              !isDomainWideMember(membership)
            ) {
              const memberRole = membership.roles?.find(
                (role) => role.name === 'MEMBER'
              );

              const newRoles: cloudidentity_v1.Schema$MembershipRole[] = [
                memberRole || { name: 'MEMBER' },
              ];

              if (selectedRole !== 'MEMBER') {
                newRoles.push({ name: selectedRole });
              }

              return {
                ...membership,
                roles: newRoles,
              };
            }
            return membership;
          });

          return newMemberships;
        });
      }

      setSelectedRole('MEMBER');
      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the selected members role(s). Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.group_details.update_member_role, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.EditGroupMemberRole.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const isCustomerMemberSelected = selectedMembers.some(isDomainWideMember);

  const isGroupMemberSelected = selectedMembers.some(
    (member) => member.type === 'GROUP'
  );

  const isOnlyCustomerMemberSelected =
    selectedRows.length === 1 &&
    selectedMembers.length === 1 &&
    isDomainWideMember(selectedMembers[0]);

  const isOnlyGroupSelected =
    selectedRows.length === 1 &&
    selectedMembers.length === 1 &&
    selectedMembers[0].type === 'GROUP';

  const RoleOptions = [
    { value: 'MEMBER', displayValue: 'Member' },
    { value: 'MANAGER', displayValue: 'Manager' },
    { value: 'OWNER', displayValue: 'Owner' },
  ];

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setSelectedRole('MEMBER');
        handleClose();
      }}
      title="Change Group Role"
      dialogContent={
        <Stack gap={2}>
          <Typography>
            {selectedRows.length > 1 ? (
              <>
                Change group role for <strong>{selectedRows.length}</strong>{' '}
                users.
              </>
            ) : isOnlyCustomerMemberSelected ? (
              `"All current and future users within domain" cannot be set to anything other than Member`
            ) : isOnlyGroupSelected ? (
              'Groups cannot be set to be Managers or Owners of a group.'
            ) : (
              <>
                Change group role for{' '}
                <strong>{selectedMembers[0]?.preferredMemberKey?.id}</strong>.
              </>
            )}
          </Typography>
          <TextField
            label="gPanel Role"
            fullWidth
            select
            value={selectedRole}
            onChange={(event) =>
              setSelectedRole(
                event.target.value as 'MEMBER' | 'MANAGER' | 'OWNER'
              )
            }
          >
            {RoleOptions.map((option) => (
              <MenuItem value={option.value} key={option.displayValue}>
                {option.displayValue}
              </MenuItem>
            ))}
          </TextField>
          <Grow
            in={
              isCustomerMemberSelected &&
              (selectedRole === 'MANAGER' || selectedRole === 'OWNER')
            }
            unmountOnExit
          >
            <Alert severity="info">
              "All current and future users within domain" cannot be set to
              anything other than Member and will be ignored.
            </Alert>
          </Grow>
          <Grow
            in={
              isGroupMemberSelected &&
              (selectedRole === 'MANAGER' || selectedRole === 'OWNER')
            }
            unmountOnExit
          >
            <Alert severity="info">
              Groups can not be set to be Managers or Owners of a group. All
              selected groups will be ignored.
            </Alert>
          </Grow>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleSubmit,
        loading: isPending,
        disabled:
          isPending || (noGroupMembers.length === 0 && selectedRows.length > 0),
      }}
    />
  );
}
